import { MouseEvent, useContext, useEffect, useState } from 'react'
import gql from 'graphql-tag'
import { useMutation } from '@apollo/react-hooks'
import { Elements, useStripe } from '@stripe/react-stripe-js'
import { get } from 'lodash'

import { getStripe } from 'utils/stripe'
import {
  sendToMobileApp,
  sendJsonToMobileApp
} from 'utils/mobile-app-publisher'
import fragments from '../graphqlFragments'
import { getContributionAttributes } from '../helpers'
import { SessionContext } from 'context/SessionProvider'
import { DigitalWalletType } from '../types'
import { amountValue } from '../LocalState'
import { PaymentLogo } from '@collegebacker/backer-ui/ui'
import styles from './DigitalWalletButton.module.scss'
import { graphQLErrorHandler } from 'utils'
import { graphQLErrorMessage } from 'utils/validation'

const CREATE_STRIPE_PAYMENT_INTENT = gql`
  mutation CreateStripePaymentIntent(
    $input: CreateStripePaymentIntentInputType!
  ) {
    createStripePaymentIntent(input: $input) {
      clientSecret
    }
  }
`
const CREATE_FULL_CONTRIBUTION_DRAFT = gql`
  mutation CreateFullContributionDraft(
    $input: CreateFullContributionDraftInputType!
  ) {
    createFullContributionDraft(input: $input) {
      ...ContributionDraftFragment
    }
  }
  ${fragments.contributionDraft}
`

const DigitalWalletButton = ({
  disabled,
  type,
  onSuccess,
  onFailure,
  fund,
  user,
  subscriptionFee
}: Props) => {
  return (
    <Elements stripe={getStripe()}>
      <DigitalWalletButtonContent
        fund={fund}
        type={type}
        user={user}
        subscriptionFee={subscriptionFee}
        onSuccess={onSuccess}
        onFailure={onFailure}
        disabled={disabled}
      />
    </Elements>
  )
}

const DigitalWalletButtonContent = ({
  disabled,
  fund,
  onSuccess,
  onFailure,
  user,
  subscriptionFee,
  type
}: Props) => {
  const context = useContext(SessionContext)
  const stripe = useStripe()
  const amount = amountValue()
  const [loading, setLoading] = useState(false)
  const [paymentRequest, setPaymentRequest] = useState(null as any)
  const partialTotal = amount + (subscriptionFee || 0)
  const processingFee = Math.ceil(0.03 * amount)
  const fullAmount = partialTotal + processingFee

  let displayItems = [
    {
      label: 'Processing Fee',
      amount: processingFee
    }
  ]

  if (subscriptionFee)
    displayItems.push({
      label: 'Backer Fee',
      amount: subscriptionFee
    })

  const paymentRequestUpdate = {
    displayItems: displayItems,
    total: {
      label: 'Backer Contribution',
      amount: fullAmount
    }
  }

  useEffect(() => {
    if (!stripe) return
    const paymentRequest = stripe?.paymentRequest({
      ...paymentRequestUpdate,
      ...{
        country: 'US',
        currency: 'usd',
        requestPayerName: true,
        requestPayerEmail: true
      }
    })

    paymentRequest.canMakePayment().then((available: any) => {
      if (available) {
        setPaymentRequest(paymentRequest)
      }
    })
  }, [!!stripe])

  const [createFullContributionDraft] = useMutation(
    CREATE_FULL_CONTRIBUTION_DRAFT
  )
  const [createStripePaymentIntent] = useMutation(CREATE_STRIPE_PAYMENT_INTENT)

  // it's important for handleClick to not be async
  // otherwise paymentRequest.show() won't work
  const handleClick = (event: MouseEvent) => {
    event.preventDefault()

    if (loading) return
    setLoading(true)

    const attributes = getContributionAttributes({
      currentStep: 'add-payment-method',
      fund,
      user
    })

    let draft = { id: 0 }
    let draftError: any | undefined
    let invokeWhenDraftReady: any = null

    function onDraftReady(fn: Function) {
      if (draft.id !== 0 || draftError) {
        fn()
      } else {
        invokeWhenDraftReady = fn
      }
    }

    function draftReady(draftResult: any, error: any) {
      if (error) {
        draftError = error
        if (onFailure) {
          onFailure(draftError)
        }
      } else {
        draft = draftResult
      }

      invokeWhenDraftReady && invokeWhenDraftReady()
    }

    createFullContributionDraft({ variables: { input: attributes } })
      .then((result) => {
        draft = get(result, 'data.createFullContributionDraft')
        draftReady(draft, false)

        if (context.mobileAppFromV(2)) {
          const root = window as any
          root.onDigitalWalletPaymentSuccess = (intent: any) => {
            setLoading(false)
            if (intent && onSuccess) onSuccess(intent, draft)
          }

          if (context.mobileAppFromV(4)) {
            sendJsonToMobileApp('charge_digital_wallet', {
              userUuid: user.uuid,
              fullAmount,
              displayItems,
              callbackFunctionName: 'window.onDigitalWalletPaymentSuccess',
              contributionDraftId: draft.id
            })
          } else {
            sendToMobileApp(
              'charge_digital_wallet',
              user.uuid,
              partialTotal,
              processingFee,
              0,
              'window.onDigitalWalletPaymentSuccess',
              draft.id
            )
          }
        }
      })
      .catch((error) => {
        draftReady(null, graphQLErrorMessage(error))
      })

    if (!context.mobileAppFromV(2)) {
      // this is necesary to avoid registering multiple events callback
      // when user clicks once then change mind and clicks a second time
      paymentRequest.removeAllListeners()
      paymentRequest.update(paymentRequestUpdate)
      // imprortant: show() should be called inside a click event (Safari)
      // we can't put this inside another async (such as canMakePayment)
      // https://stripe.com/docs/js/payment_request/show
      paymentRequest.show()
      paymentRequest.on('cancel', () => setLoading(false))
      paymentRequest.on('paymentmethod', async (event: any) => {
        onDraftReady(async () => {
          if (draftError) {
            setLoading(false)
            event.complete('fail')
            if (onFailure) onFailure(draftError)
          } else {
            try {
              const {
                data: {
                  createStripePaymentIntent: { clientSecret }
                }
              } = await createStripePaymentIntent({
                variables: {
                  input: {
                    userUuid: user.uuid,
                    contributionDraftId: draft.id,
                    amount: fullAmount
                  }
                }
              })
              // @ts-ignore
              const { paymentIntent, error } = await stripe?.confirmCardPayment(
                clientSecret,
                {
                  payment_method: event.paymentMethod.id
                }
              )

              setLoading(false)

              if (error) {
                console.error({ error })
                event.complete('fail')
                if (onFailure) onFailure(error)
              } else {
                event.complete('success')
                // Check if the PaymentIntent requires any actions and if so let Stripe.js handle the flow.
                if (paymentIntent.status === 'requires_action') {
                  //@ts-ignore
                  const { error } = await stripe?.confirmCardPayment(
                    clientSecret
                  )
                  if (error) {
                    // @TODO - The payment failed -- ask your customer for a new payment method.
                    event.complete('fail')
                    if (onFailure) onFailure('' + error)
                  } else {
                    if (onSuccess && paymentIntent) {
                      onSuccess(paymentIntent, draft)
                    }
                  }
                } else {
                  if (onSuccess && paymentIntent) {
                    onSuccess(paymentIntent, draft)
                  }
                }
              }
            } catch (error) {
              console.error('ERROR>>> payment failed: ', error)
              event.complete('fail')
              if (onFailure) onFailure('' + error)
            }
          }
        })
      })
    }
  }
  return (
    <Elements stripe={getStripe()}>
      <button
        disabled={disabled || loading}
        type="submit"
        onClick={handleClick}
        className={styles.digitalWalletBtn}
      >
        Contribute with
        <PaymentLogo
          className={styles.paymentLogo}
          name={type === 'applePay' ? 'apple-pay' : 'g-pay'}
        />
      </button>
    </Elements>
  )
}

interface Props {
  type: DigitalWalletType
  subscriptionFee?: number
  onSuccess?: (arg0: any, arg1: any) => void
  onFailure?: (arg0: any) => void
  fund: any
  user: any
  disabled?: boolean
}

export default DigitalWalletButton
